import { useEffect, useRef, useState } from "react";

type TypingOptions = {
  // Called when typing completes
  onComplete?: () => void;
  // If true, parse content into HTML-aware tokens; otherwise type per character
  htmlAware?: boolean;
  // Minimum delay between tokens/characters
  minDelayMs?: number;
  // Maximum additional random delay added to minDelay
  randomRangeMs?: number;
  // Fixed delay (overrides min/random) if provided
  fixedDelayMs?: number;
  // Optional leading pause before starting
  leadingPauseMs?: number;
};

export function useTypingEffect(
  content: string,
  isStreaming: boolean,
  options: TypingOptions = {},
) {
  const [displayed, setDisplayed] = useState<string>("");
  const [typing, setTyping] = useState<boolean>(false);
  const timeoutRef = useRef<number | null>(null);
  const hasStreamedRef = useRef<boolean>(false);
  const previousContentRef = useRef<string>("");

  const {
    onComplete,
    htmlAware = false,
    minDelayMs = 20,
    randomRangeMs = 30,
    fixedDelayMs,
    leadingPauseMs = 0,
  } = options;

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Reset when content changes completely
    if (content !== previousContentRef.current && displayed !== "") {
      hasStreamedRef.current = false;
      setTyping(false);
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }

    // If not streaming, immediately set displayed
    if (!isStreaming) {
      previousContentRef.current = content;
      if (displayed !== content) setDisplayed(content);
      return;
    }

    if (!content || typing || hasStreamedRef.current) return;

    hasStreamedRef.current = true;
    setTyping(true);

    const tokens = htmlAware
      ? parseContentIntoHtmlAwareTokens(content)
      : Array.from(content);

    let index = 0;
    let acc = "";

    const scheduleNext = () => {
      const delay =
        fixedDelayMs !== undefined
          ? fixedDelayMs
          : minDelayMs + Math.random() * randomRangeMs;
      timeoutRef.current = window.setTimeout(step, delay);
    };

    const step = () => {
      if (index < tokens.length) {
        const token = tokens[index];
        if (index === 0) {
          acc = token;
        } else {
          if (htmlAware) {
            const isCurrentTokenHtml =
              token.startsWith("<") && token.endsWith(">");
            const prev = tokens[index - 1];
            const isPrevHtml = prev.startsWith("<") && prev.endsWith(">");
            if (!isCurrentTokenHtml && !isPrevHtml && token !== " ") {
              acc += " ";
            }
          }
          acc += token;
        }
        setDisplayed(acc);
        index += 1;
        scheduleNext();
      } else {
        setTyping(false);
        previousContentRef.current = content;
        if (onComplete) onComplete();
      }
    };

    if (leadingPauseMs > 0) {
      timeoutRef.current = window.setTimeout(step, leadingPauseMs);
    } else {
      step();
    }

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [content, isStreaming]);

  return { displayed, typing } as const;
}

function parseContentIntoHtmlAwareTokens(content: string): string[] {
  const tokens: string[] = [];
  let currentIndex = 0;
  while (currentIndex < content.length) {
    if (content[currentIndex] === "<") {
      const tagEndIndex = content.indexOf(">", currentIndex);
      if (tagEndIndex !== -1) {
        const htmlToken = content.substring(currentIndex, tagEndIndex + 1);
        tokens.push(htmlToken);
        currentIndex = tagEndIndex + 1;
      } else {
        tokens.push(content[currentIndex]);
        currentIndex++;
      }
    } else {
      let wordEndIndex = currentIndex;
      while (
        wordEndIndex < content.length &&
        content[wordEndIndex] !== " " &&
        content[wordEndIndex] !== "<"
      ) {
        wordEndIndex++;
      }
      const word = content.substring(currentIndex, wordEndIndex);
      if (word.trim()) tokens.push(word);
      if (wordEndIndex < content.length && content[wordEndIndex] === " ") {
        tokens.push(" ");
      }
      currentIndex = wordEndIndex + (content[wordEndIndex] === " " ? 1 : 0);
    }
  }
  return tokens;
}
